<script setup lang="ts">
import BaseDropdownItem from "@/components/Base/Dropdown/Item/index.vue";
import BaseIcon from "@/components/Base/Icon/index.vue";
import IconButton from "@/components/IconButton.vue";
import type { MenuItem } from "@/types/folders";
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";

// eslint-disable-next-line import/no-self-import
import BaseDropdownMenu from "./Menu.vue";

export type MenuDirection = "left" | "right";

const props = defineProps<{
  items: MenuItem[];
  direction?: MenuDirection;
}>();

const dir = ref<MenuDirection>(props.direction ?? "left");
const menuEl = ref<HTMLElement | null>(null);

const updateDirection = (element?: HTMLElement) => {
  const el = element || menuEl.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();

  const spaceRight = window.innerWidth - rect.right;
  const spaceLeft = rect.left;

  dir.value = spaceLeft > spaceRight ? "left" : "right";
};

if (props.direction === undefined) {
  updateDirection();
  onMounted(() => {
    nextTick(() => updateDirection());
    window.addEventListener("resize", () => updateDirection());
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", () => updateDirection());
  });
} else {
  dir.value = props.direction;
}
// expose functions for testing
defineExpose({
  updateDirection,
  menuEl,
});
</script>

<template>
  <ul
    ref="menuEl"
    class="dropdown-menu"
    role="menu"
    aria-orientation="vertical"
  >
    <template v-for="(item, i) in items" :key="`menu-item-${i}`">
      <!-- Render divider -->
      <li v-if="item.divider" class="dropdown-divider"></li>

      <!-- Render nested dropdown items -->
      <li
        v-else-if="item.children?.length"
        class="dropdown-submenu"
        :class="{
          'dropdown-submenu--opens-right': dir === 'right',
          disabled: item.disabled,
        }"
        tabindex="0"
        role="menuitem"
        aria-haspopup="true"
        @mouseenter="
          props.direction === undefined &&
          updateDirection($event.currentTarget as HTMLElement)
        "
        @focusin="
          props.direction === undefined &&
          updateDirection($event.currentTarget as HTMLElement)
        "
        @keydown.enter="($event.target as HTMLElement)?.classList?.add('show')"
        @keydown.space="($event.target as HTMLElement)?.classList?.add('show')"
        @keydown.escape="
          ($event.target as HTMLElement)?.classList?.remove('show')
        "
      >
        <div
          class="dropdown-item py-1 px-2"
          :class="{ 'pe-none': item.disabled, 'ps-2': item.icon }"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <IconButton
            icon-position="left"
            size="sm"
            type="button"
            :icon-name="item.icon"
            :icon-bg-color="item.iconColor"
            variant="dark"
            :disabled="item.disabled"
          >
            {{ item.label }}
          </IconButton>

          <BaseIcon
            v-if="!item.disabled"
            icon-name="chevron-right"
            class="ms-auto"
          />
        </div>
        <BaseDropdownMenu :items="item.children" :direction="dir" />
      </li>

      <!-- Render regular menu items -->
      <BaseDropdownItem
        v-else
        :label="item.label"
        :icon-name="item.icon"
        :icon-bg-color="item.iconColor"
        :to="item.to"
        :href="item.href"
        :class="{ disabled: item.disabled }"
        role="menuitem"
        tabindex="0"
        @click.prevent="item.onClick?.()"
        @keydown.enter="item.onClick?.()"
        @keydown.space="item.onClick?.()"
      />
    </template>
  </ul>
</template>

<style scoped lang="scss">
.dropdown-divider {
  margin: 0.5rem 0;
}

.dropdown-submenu {
  position: relative;

  .dropdown-menu {
    top: 0;
    left: 100%;
    min-width: 12rem;
    margin-top: -0.5rem;
    display: none;
  }

  &:hover > .dropdown-menu,
  &.show > .dropdown-menu {
    display: block;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: default;

    &.dropdown-toggle::after {
      display: none;
    }
  }
}

.dropdown-submenu:hover > .dropdown-menu {
  position: absolute;
  display: block;
  right: 100%;
  left: auto;
}

.dropdown-submenu.dropdown-submenu--opens-right:hover > .dropdown-menu {
  right: auto;
  left: 100%;
}

.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
</style>
